import React, { memo, useEffect, useMemo, useRef, useState } from '../../lib/teact/teact';
import { getActions } from '../../global';

import type { ApiWalletMetadataSharing } from '../../api/types';
import type { Account } from '../../global/types';

import { dropEnclaveSessionHold, holdEnclaveSession } from '../../global/helpers/enclave';
import { parseAccountId } from '../../util/account';
import buildClassName from '../../util/buildClassName';
import { logDebugError } from '../../util/logs';
import { shortenAddress } from '../../util/shortenAddress';
import { WALLET_METADATA_DIRECTORY_CHANGED_EVENT } from '../../util/walletMetadataDirectory';
import { callApiWithThrow } from '../../api';

import useLang from '../../hooks/useLang';
import useLastCallback from '../../hooks/useLastCallback';
import { notifyWalletMetadataDirectoryChanged } from '../../hooks/useWalletMetadataDirectory';

import Button from '../ui/Button';
import Modal from '../ui/Modal';
import ModalHeader from '../ui/ModalHeader';
import OverlayScrollbar from '../ui/OverlayScrollbar';
import PasswordForm from '../ui/PasswordForm';
import Radio from '../ui/Radio';
import Spinner from '../ui/Spinner';
import MetadataSharingHelp from './MetadataSharingHelp';

import identityStyles from '../common/MetadataIdentity.module.scss';
import settingsStyles from './Settings.module.scss';
import styles from './SettingsMetadata.module.scss';

type OwnProps = {
  accountId: string;
  accounts?: Record<string, Account>;
  accountIds: string[];
  isActive?: boolean;
};
type Notice = 'link_pending' | 'leave_pending' | 'link_done' | 'leave_done';
type WalletChoice = { value: string; address: string; name: string; description: string };

function groupWalletChoices(
  choices: WalletChoice[], sharings: Record<string, ApiWalletMetadataSharing | false>, network: string,
) {
  const groups = choices.map((choice) => {
    const sharing = sharings[choice.value];
    return {
      choices: [choice],
      members: new Set([
        choice.address,
        ...(sharing ? sharing.groups : [])
          .filter((group) => group.network === network && group.members.includes(choice.address))
          .flatMap((group) => group.members),
      ]),
    };
  });
  for (let left = 0; left < groups.length; left++) {
    for (let right = groups.length - 1; right > left; right--) {
      if (![...groups[right].members].some((member) => groups[left].members.has(member))) continue;
      groups[left].choices.push(...groups[right].choices);
      groups[right].members.forEach((member) => groups[left].members.add(member));
      groups.splice(right, 1);
    }
  }
  return groups;
}

function SettingsMetadataSharing({ accountId, accounts, accountIds, isActive = true }: OwnProps) {
  const lang = useLang();
  const [sharing, setSharing] = useState<ApiWalletMetadataSharing>();
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [showSelector, setShowSelector] = useState(false);
  const [target, setTarget] = useState<string>();
  const [isConfirmingLeave, setIsConfirmingLeave] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [notice, setNotice] = useState<Notice>();
  const [authorizationTarget, setAuthorizationTarget] = useState<string>();
  const [isAuthorizingLeave, setIsAuthorizingLeave] = useState(false);
  const [authorizationError, setAuthorizationError] = useState('');
  const [candidateSharings, setCandidateSharings] = useState<Record<string, ApiWalletMetadataSharing | false>>({});
  const requestRef = useRef(0);
  const previewRequestRef = useRef(0);
  const operationRef = useRef(false);
  const lifecycleRef = useRef(0);
  const mountedRef = useRef(true);
  const address = accounts?.[accountId]?.byChain.tron?.address;
  const { network } = parseAccountId(accountId);
  const isEligible = accountIds.includes(accountId);
  const accountsKey = JSON.stringify(accountIds.map((id) => [id, accounts?.[id]?.byChain.tron?.address]));
  const members = useMemo(() => [...new Set((sharing?.groups ?? [])
    .filter((group) => group.network === network).flatMap((group) => group.members))], [sharing, network]);
  const walletName = (member: string) => (member === address ? accounts?.[accountId]?.title : undefined)
    || Object.entries(accounts ?? {}).find(([id, account]) => (
      parseAccountId(id).network === network && account.byChain.tron?.address === member && account.title
    ))?.[1].title || shortenAddress(member) || member;
  const connected = [...new Set([...(address ? [address] : []), ...members])]
    .sort((a, b) => a === address ? -1 : b === address ? 1
      : walletName(a).localeCompare(walletName(b)));
  const candidates = useMemo(() => {
    const seen = new Set(members);
    if (address) seen.add(address);
    return accountIds.flatMap((id) => {
      const account = accounts?.[id];
      const candidate = account?.byChain.tron?.address;
      if (account?.type !== 'mnemonic' || account.isTemporary || account.isRecoveryRequired || !candidate
        || candidate === address
        || parseAccountId(id).network !== network
        || (seen.has(candidate) && !(id === target && (notice === 'link_pending' || isWorking)))) return [];
      seen.add(candidate);
      return [{ value: id, address: candidate, name: account.title || shortenAddress(candidate) || candidate,
        description: shortenAddress(candidate) || candidate }];
    });
  }, [accountIds, accounts, address, members, network, target, notice, isWorking]);
  const selectedTarget = candidates.some((item) => item.value === target) ? target : candidates[0]?.value;
  const selectedTargetAddress = selectedTarget ? accounts?.[selectedTarget]?.byChain.tron?.address : undefined;
  const selectedTargetSharing = selectedTarget ? candidateSharings[selectedTarget] : undefined;
  const targetSharing = selectedTargetSharing || undefined;
  const choiceGroups = useMemo(
    () => groupWalletChoices(candidates, candidateSharings, network),
    [candidates, candidateSharings, network],
  );
  const candidateIdsKey = JSON.stringify(candidates.map(({ value }) => value));
  const targetMembers = [...new Set([
    ...(selectedTargetAddress ? [selectedTargetAddress] : []),
    ...(targetSharing?.groups ?? [])
      .filter((group) => group.network === network && selectedTargetAddress
        && group.members.includes(selectedTargetAddress))
      .flatMap((group) => group.members),
  ])];
  const previewMembers = [...new Set([
    ...connected,
    ...targetMembers,
  ])].sort((a, b) => a === address ? -1 : b === address ? 1 : walletName(a).localeCompare(walletName(b)));
  const shouldShowMergePreview = connected.length > 1 && targetMembers.length > 1;
  const isInitialLoading = isLoading && !sharing && !loadFailed;

  const reload = useLastCallback(async () => {
    if (!isActive || !isEligible) return;
    const revision = ++requestRef.current;
    setIsLoading(true);
    try {
      const value = await callApiWithThrow('fetchWalletMetadataSharing', accountId);
      if (mountedRef.current && revision === requestRef.current) {
        setSharing(value);
        setLoadFailed(false);
      }
    } catch (error) {
      logDebugError('Wallet metadata sharing refresh', error);
      if (mountedRef.current && revision === requestRef.current) setLoadFailed(true);
    } finally {
      if (mountedRef.current && revision === requestRef.current) setIsLoading(false);
    }
  });

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);
  useEffect(() => {
    if (!isActive) {
      lifecycleRef.current += 1;
      setAuthorizationTarget(undefined);
      setIsAuthorizingLeave(false);
      setAuthorizationError('');
    }
  }, [isActive]);
  useEffect(() => {
    if (!isActive || !isEligible) return undefined;
    const refresh = () => {
      void reload();
    };
    refresh();
    window.addEventListener(WALLET_METADATA_DIRECTORY_CHANGED_EVENT, refresh);
    window.addEventListener('online', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      requestRef.current += 1;
      window.removeEventListener(WALLET_METADATA_DIRECTORY_CHANGED_EVENT, refresh);
      window.removeEventListener('online', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [isActive, isEligible, accountsKey]);
  useEffect(() => {
    const revision = ++previewRequestRef.current;
    const candidateIds = JSON.parse(candidateIdsKey) as string[];
    if (!isActive || !showSelector || !candidateIds.length) {
      setCandidateSharings({});
      return;
    }
    setCandidateSharings({});
    candidateIds.forEach((candidateId) => {
      void callApiWithThrow('fetchWalletMetadataSharing', candidateId).then((value) => {
        if (mountedRef.current && revision === previewRequestRef.current) {
          setCandidateSharings((current) => ({ ...current, [candidateId]: value }));
        }
      }).catch((error) => {
        logDebugError('Wallet metadata merge preview', error);
        if (mountedRef.current && revision === previewRequestRef.current) {
          setCandidateSharings((current) => ({ ...current, [candidateId]: false }));
        }
      });
    });
  }, [isActive, showSelector, candidateIdsKey]);

  const changeLink = useLastCallback(async (leave: boolean, enclaveToken?: string) => {
    const linkTarget = enclaveToken && !leave ? authorizationTarget : selectedTarget;
    if (!mountedRef.current || !isActive || operationRef.current
      || (!leave && !linkTarget) || (!enclaveToken && authorizationTarget)) return;
    operationRef.current = true;
    const lifecycle = lifecycleRef.current;
    setIsWorking(true);
    if (!leave) setTarget(linkTarget);
    setNotice(undefined);
    setAuthorizationError('');
    try {
      let success: boolean | 'authorization_required';
      if (leave) {
        success = enclaveToken
          ? await callApiWithThrow('leaveWalletMetadataSharing', accountId, enclaveToken)
          : await callApiWithThrow('leaveWalletMetadataSharing', accountId);
      } else if (linkTarget) {
        success = enclaveToken
          ? await callApiWithThrow('linkWalletMetadataSharing', accountId, linkTarget, enclaveToken)
          : await callApiWithThrow('linkWalletMetadataSharing', accountId, linkTarget);
      } else {
        return;
      }
      if (!mountedRef.current || lifecycle !== lifecycleRef.current) return;
      if (success === 'authorization_required') {
        setAuthorizationTarget(leave ? accountId : linkTarget);
        setIsAuthorizingLeave(leave);
        if (enclaveToken) setAuthorizationError(lang('$metadata_sharing_locked'));
        return;
      }
      setAuthorizationTarget(undefined);
      setIsAuthorizingLeave(false);
      notifyWalletMetadataDirectoryChanged();
      setNotice(leave ? success ? 'leave_done' : 'leave_pending' : success ? 'link_done' : 'link_pending');
      if (success) {
        setShowSelector(false);
        setTarget(undefined);
        setIsConfirmingLeave(false);
      }
    } catch (error) {
      logDebugError('Wallet metadata sharing update', error);
      if (mountedRef.current && lifecycle === lifecycleRef.current) {
        setAuthorizationTarget(undefined);
        setIsAuthorizingLeave(false);
        setNotice(leave ? 'leave_pending' : 'link_pending');
      }
    } finally {
      operationRef.current = false;
      if (mountedRef.current) setIsWorking(false);
    }
  });

  const authorizeChange = useLastCallback(async (enclaveToken: string) => {
    holdEnclaveSession(enclaveToken);
    try {
      await changeLink(isAuthorizingLeave, enclaveToken);
    } finally {
      if (dropEnclaveSessionHold(enclaveToken)) getActions().releaseEnclaveSession({ enclaveToken });
    }
  });

  const closeAuthorization = useLastCallback(() => {
    if (operationRef.current) return;
    setAuthorizationTarget(undefined);
    setIsAuthorizingLeave(false);
    setAuthorizationError('');
  });

  if (!isEligible) return undefined;
  return (
    <section aria-label={lang('$metadata_linked_wallets')} data-telemetry-block="">
      <div className={settingsStyles.blockTitleRow}>
        <h3 className={settingsStyles.blockTitle}>{lang('$metadata_linked_wallets')}</h3>
        <MetadataSharingHelp />
      </div>
      <div
        className={buildClassName(settingsStyles.block,
          sharing?.is_locked && settingsStyles.settingsBlockWithDescription)}
        role="list"
      >
        {isInitialLoading && (
          <div className={styles.empty} role="listitem">
            <Spinner />
          </div>
        )}
        {!isInitialLoading && connected.map((member) => (
          <div
            className={buildClassName(settingsStyles.item, settingsStyles.item_nonInteractive, styles.record)}
            key={member}
            role="listitem"
          >
            <i className={buildClassName(identityStyles.avatar, styles.walletIcon, 'icon-wallet')} aria-hidden />
            <span className={buildClassName(settingsStyles.itemContent, styles.recordContent)}>
              <span className={settingsStyles.itemTitle}>{walletName(member)}</span>
              <span className={settingsStyles.itemSubtitle}>{shortenAddress(member)}</span>
            </span>
            {member === address && (
              <span className={styles.currentWallet}>{lang('$metadata_current_wallet')}</span>
            )}
          </div>
        ))}
        {!isInitialLoading && !connected.length && (
          <div className={styles.empty} role="listitem">{lang('$metadata_no_linked_wallets')}</div>
        )}
        {!isInitialLoading && !showSelector && (
          <div className={styles.sharingActions} role="listitem">
            <button
              type="button"
              className={buildClassName(settingsStyles.item, settingsStyles.item_small, styles.action)}
              aria-label={lang('$metadata_link_wallet')}
              disabled={isWorking || !candidates.length}
              onClick={() => {
                setShowSelector(true);
                setIsConfirmingLeave(false);
                setNotice(undefined);
              }}
            >
              <span className={buildClassName(settingsStyles.itemTitle, settingsStyles.itemTitle_accent)}>
                {lang('$metadata_link_wallet')}
              </span>
              <i className={buildClassName(settingsStyles.iconChevronRight, 'icon-chevron-right')} aria-hidden />
            </button>
            {address && members.includes(address) && !isConfirmingLeave && (
              <button
                type="button"
                className={buildClassName(settingsStyles.item, settingsStyles.item_small,
                  settingsStyles.item_red, styles.action)}
                disabled={isWorking}
                onClick={() => {
                  setIsConfirmingLeave(true);
                  setNotice(undefined);
                }}
              >
                <span className={settingsStyles.itemTitle}>{lang('$metadata_unlink_wallet')}</span>
                <i className="icon-exit" aria-hidden />
              </button>
            )}
          </div>
        )}
      </div>
      {sharing?.is_locked && (
        <p className={buildClassName(settingsStyles.blockDescription, styles.sharingNotice)} role="status">
          <i className="icon-lock" aria-hidden /> {lang('$metadata_sharing_locked')}
        </p>
      )}
      {loadFailed && <p className={styles.error} role="alert">{lang('$metadata_sharing_load_failed')}</p>}
      {showSelector && (
        <div className={styles.linkSelector}>
          <h3 className={settingsStyles.blockTitle}>{lang('$metadata_link_wallet')}</h3>
          {candidates.length ? (
            <fieldset className={buildClassName(settingsStyles.block, styles.walletChoices)} disabled={isWorking}>
              <legend className="visually-hidden">{lang('$metadata_link_wallet')}</legend>
              <OverlayScrollbar isActive={isActive && showSelector}>
                {choiceGroups.map((group) => {
                  const choiceAddresses = new Set(group.choices.map(({ address: choiceAddress }) => choiceAddress));
                  const peerMembers = [...group.members].filter((member) => !choiceAddresses.has(member));
                  const isLinkedGroup = group.members.size > 1;
                  const isGroupSelected = group.choices.some(({ value }) => value === selectedTarget);
                  const selectTarget = (value: string) => {
                    if (isWorking) return;
                    setTarget(value);
                    setNotice(undefined);
                  };
                  if (isLinkedGroup) {
                    return (
                      <label
                        key={group.choices[0].value}
                        className={buildClassName(styles.walletChoiceGroup, styles.walletChoiceGroupLinked)}
                        aria-label={lang('$metadata_linked_wallets')}
                        data-wallet-group="linked"
                      >
                        <div className={styles.walletChoiceGroupTitle}>
                          <span>
                            <i className="icon-link" aria-hidden />
                            {lang('$metadata_linked_wallets')}
                          </span>
                          <Radio
                            name={`metadata-link-${accountId}`}
                            value={group.choices[0].value}
                            isChecked={isGroupSelected}
                            className={styles.walletChoiceGroupRadio}
                            onChange={(event) => selectTarget(event.currentTarget.value)}
                          />
                        </div>
                        <div className={styles.walletChoiceGroupMembers}>
                          {group.choices.map((candidate) => (
                            <div className={styles.walletChoiceGroupMember} key={candidate.value}>
                              <i
                                className={buildClassName(identityStyles.avatar, styles.walletIcon, 'icon-wallet')}
                                aria-hidden
                              />
                              <span className={buildClassName(settingsStyles.itemContent, styles.recordContent)}>
                                <span className={settingsStyles.itemTitle}>{candidate.name}</span>
                                <span className={settingsStyles.itemSubtitle}>{candidate.description}</span>
                              </span>
                            </div>
                          ))}
                          {peerMembers.map((member) => (
                            <div className={styles.walletChoicePeer} key={member}>
                              <i
                                className={buildClassName(identityStyles.avatar, styles.walletIcon, 'icon-wallet')}
                                aria-hidden
                              />
                              <span className={buildClassName(settingsStyles.itemContent, styles.recordContent)}>
                                <span className={settingsStyles.itemTitle}>{walletName(member)}</span>
                                <span className={settingsStyles.itemSubtitle}>{shortenAddress(member)}</span>
                              </span>
                              {member === address && (
                                <i className="icon-check" aria-label={lang('$metadata_current_wallet')} />
                              )}
                            </div>
                          ))}
                        </div>
                      </label>
                    );
                  }
                  const candidate = group.choices[0];
                  return (
                    <label
                      key={candidate.value}
                      className={buildClassName(settingsStyles.item, styles.record, styles.walletChoice,
                        styles.walletChoiceGroup)}
                      data-wallet-group="single"
                    >
                      <i
                        className={buildClassName(identityStyles.avatar, styles.walletIcon, 'icon-wallet')}
                        aria-hidden
                      />
                      <span className={buildClassName(settingsStyles.itemContent, styles.recordContent)}>
                        <span className={settingsStyles.itemTitle}>{candidate.name}</span>
                        <span className={settingsStyles.itemSubtitle}>{candidate.description}</span>
                      </span>
                      <Radio
                        name={`metadata-link-${accountId}`}
                        value={candidate.value}
                        isChecked={candidate.value === selectedTarget}
                        className={styles.walletChoiceRadio}
                        onChange={(event) => selectTarget(event.currentTarget.value)}
                      />
                    </label>
                  );
                })}
              </OverlayScrollbar>
            </fieldset>
          ) : (
            <p className={buildClassName(settingsStyles.blockDescription, styles.sharingNotice)}>
              {lang('$metadata_no_link_candidates')}
            </p>
          )}
          {selectedTarget && shouldShowMergePreview && (
            <div className={styles.mergePreview} aria-label={lang('$metadata_merge_groups')}>
              <div className={styles.mergePreviewTitle}>
                <i className="icon-link" aria-hidden />
                <span>{lang('$metadata_merge_groups')}</span>
              </div>
              <div className={styles.mergePreviewMembers}>
                {previewMembers.map((member) => (
                  <div className={styles.mergePreviewMember} key={member}>
                    <i
                      className={buildClassName(identityStyles.avatar, styles.previewWalletIcon, 'icon-wallet')}
                      aria-hidden
                    />
                    <span>{walletName(member)}</span>
                    {member === address && <i className="icon-check" aria-label={lang('$metadata_current_wallet')} />}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className={styles.sharingButtons}>
            <Button isSecondary isDisabled={isWorking} onClick={() => setShowSelector(false)}>{lang('Cancel')}</Button>
            <Button isPrimary isLoading={isWorking} isDisabled={!selectedTarget} onClick={() => changeLink(false)}>
              <i className="icon-link" aria-hidden /><span>{lang('$metadata_link_wallet')}</span>
            </Button>
          </div>
        </div>
      )}
      {isConfirmingLeave && (
        <div className={styles.deleteConfirmation}>
          <p>{lang('$metadata_leave_confirm')}</p>
          <div className={styles.sharingButtons}>
            <Button isSecondary isDisabled={isWorking} onClick={() => setIsConfirmingLeave(false)}>
              {lang('Cancel')}
            </Button>
            <Button isDestructive isLoading={isWorking} onClick={() => changeLink(true)}>
              {lang('$metadata_unlink_wallet')}
            </Button>
          </div>
        </div>
      )}
      {notice && (
        <p
          className={buildClassName(settingsStyles.blockDescription, settingsStyles.noTopMargin, styles.sharingNotice)}
          role="status"
        >
          {lang(`$metadata_${notice}`)}
        </p>
      )}
      <Modal
        isOpen={isActive && Boolean(authorizationTarget)}
        header={(
          <ModalHeader
            title={lang(isAuthorizingLeave ? '$metadata_unlink_wallet' : '$metadata_link_wallet')}
            closeClassName={styles.authorizationClose}
            onClose={closeAuthorization}
          />
        )}
        onClose={closeAuthorization}
      >
        {authorizationTarget && (
          <PasswordForm
            isActive={isActive}
            isLoading={isWorking}
            operationType="passcode"
            extraAuthUsages={1}
            submitLabel={lang(isAuthorizingLeave ? '$metadata_unlink_wallet' : '$metadata_link_wallet')}
            error={authorizationError}
            noAutoConfirm
            onAuthorize={authorizeChange}
            onCancel={closeAuthorization}
            onUpdate={() => setAuthorizationError('')}
          />
        )}
      </Modal>
    </section>
  );
}

export default memo(SettingsMetadataSharing);
